import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import CardComponent from "../../components/card/CardComponent";
import { ReactComponent as PurchaseIcon } from "../../assets/PurchaseLogoHome.svg";
import { ReactComponent as RefinanceIcon } from "../../assets/RefinanceLogoHome.svg";
import appointmenticon from "../../assets/appointment.png";
import { useDispatch, useSelector } from "react-redux";
import { setFormType, setSessionId } from "../../redux/actions";
import "./home.css";
import { getSessionId } from "../../services/formService";
import { extractUrlParameters, preserveAllUrlParams, clearAndRestoreParamsFromUrl, restoreDynamicParamsFromStorage } from "../../utils/urlParams";
import useIsInIframe from "../../hooks/useIsInIframe";

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isInIframe = useIsInIframe();

  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("language");
    return saved === "Spanish" ? "Spanish" : "English";
  });

  const sessionId = useSelector(state => state.sessionId)


  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    let needsUpdate = false;

    if (!searchParams.has("lar")) {
      searchParams.set("lar", "admin");
      localStorage.setItem("lar", "admin")
      needsUpdate = true;
    } else {
      localStorage.setItem("lar", searchParams.get("lar"))
    }
    const utmSourceFromUrl = searchParams.get("utm_source");
    if (!utmSourceFromUrl) {
      searchParams.set("utm_source", "direct");
      localStorage.setItem("utm_source", "direct")
      needsUpdate = true;
    } else {
      searchParams.set("utm_source", utmSourceFromUrl);
      localStorage.setItem("utm_source", utmSourceFromUrl)
    }

    const utmTermFromUrl = searchParams.get("utm_term");
    if (utmTermFromUrl) {
      searchParams.set("utm_term", utmTermFromUrl);
      localStorage.setItem("utm_term", utmTermFromUrl);
    } else {
      const utmTermFromStorage = localStorage.getItem("utm_term");
      if (utmTermFromStorage) {
        searchParams.set("utm_term", utmTermFromStorage);
        needsUpdate = true;
      } else {
        localStorage.removeItem("utm_term");
      }
    }

    const utmCampaignFromUrl = searchParams.get("utm_campaign");
    if (utmCampaignFromUrl) {
      searchParams.set("utm_campaign", utmCampaignFromUrl);
      localStorage.setItem("utm_campaign", utmCampaignFromUrl);
    } else {
      const utmCampaignFromStorage = localStorage.getItem("utm_campaign");
      if (utmCampaignFromStorage) {
        searchParams.set("utm_campaign", utmCampaignFromStorage);
        needsUpdate = true;
      } else {
        localStorage.removeItem("utm_campaign");
      }
    }

    const utmTypeFromUrl = searchParams.get("utm_type");
    if (utmTypeFromUrl) {
      searchParams.set("utm_type", utmTypeFromUrl);
      localStorage.setItem("utm_type", utmTypeFromUrl);
    } else {
      const utmTypeFromStorage = localStorage.getItem("utm_type");
      if (utmTypeFromStorage) {
        searchParams.set("utm_type", utmTypeFromStorage);
        needsUpdate = true;
      } else {
        localStorage.removeItem("utm_type");
      }
    }
    
    // Store dynamic parameters in localStorage
    const { dynamicParameters } = extractUrlParameters(searchParams);
    if (dynamicParameters && dynamicParameters.length > 0) {
      localStorage.setItem("dynamic_parameters", JSON.stringify(dynamicParameters));
    } else {
      // If no dynamic parameters in URL, try to restore from storage
      const dynamicParamsFromStorage = localStorage.getItem("dynamic_parameters");
      if (dynamicParamsFromStorage) {
        try {
          const parsed = JSON.parse(dynamicParamsFromStorage);
          if (Array.isArray(parsed) && parsed.length > 0) {
            parsed.forEach((param) => {
              if (param.key && param.value && !searchParams.has(param.key)) {
                searchParams.set(param.key, param.value);
                needsUpdate = true;
              }
            });
          }
        } catch (error) {
          console.error("Error parsing dynamic parameters from localStorage:", error);
        }
      }
    }

    if (needsUpdate) {
      navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const initializeSession = async () => {
      try {
        let currentSessionId = sessionId;

        if (!currentSessionId) {
          // When creating a new session, clear and re-store all parameters from URL
          const { registeredParams, dynamicParameters } = clearAndRestoreParamsFromUrl(location.search);
          
          const lar = registeredParams.lar || localStorage.getItem("lar") || "admin";
          const source = registeredParams.utm_source || localStorage.getItem("utm_source") || "direct";
          const kw = registeredParams.utm_term || localStorage.getItem("utm_term") || "";
          const campaign = registeredParams.utm_campaign || localStorage.getItem("utm_campaign") || "";
          const language = localStorage.getItem("language") || "English";
          currentSessionId = await getSessionId(lar, source, kw, campaign, language, dynamicParameters);
          dispatch(setSessionId(currentSessionId));
        }
      } catch (error) {
        console.error("Failed to initialize session:", error);
      }
    };
    initializeSession();
  }, [location.search, sessionId, dispatch]);

  useEffect(() => {
    if (isInIframe) {
      document.body.classList.add("home-page");
      document.documentElement.classList.add("home-page");
    }

    return () => {
      document.body.classList.remove("home-page");
      document.documentElement.classList.remove("home-page");
    };
  }, [isInIframe]);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === "language") {
        setLang(e.newValue === "Spanish" ? "Spanish" : "English");
      }
    };

    const handleLangChanged = (e) => {
      const next = e?.detail;
      setLang(next === "Spanish" ? "Spanish" : "English");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener("langChanged", handleLangChanged);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("langChanged", handleLangChanged);
    };
  }, []);

  const handleCardClick = (flowType) => {
    // Preserve all URL parameters (both registered and dynamic)
    const searchParams = preserveAllUrlParams(location.search);
    
    // Restore dynamic parameters from localStorage if not in URL
    restoreDynamicParamsFromStorage(searchParams);
    
    // Ensure required parameters have defaults if missing
    if (!searchParams.has("lar")) {
      searchParams.set("lar", "admin");
    }
    if (!searchParams.has("utm_source")) {
      searchParams.set("utm_source", "direct");
    }
    
    // Handle utm_type from localStorage if not in URL
    if (!searchParams.has("utm_type")) {
      const utmTypeFromStorage = localStorage.getItem("utm_type");
      if (utmTypeFromStorage) {
        searchParams.set("utm_type", utmTypeFromStorage);
      }
    }

    if (flowType === "purchase") {
      dispatch(setFormType("purchase"));
      navigate(
        `${process.env.REACT_APP_PURCHASE_URL}?${searchParams.toString()}`
      );
    } else if (flowType === "refinance") {
      dispatch(setFormType("refinance"));
      navigate(
        `${process.env.REACT_APP_REFINANCE_URL}?${searchParams.toString()}`
      );
    } else if (flowType === "appointment") {
      dispatch(setFormType("appointment"));
      navigate(
        `${process.env.REACT_APP_MEETINGINFO_URL}?${searchParams.toString()}`);
    }
  };

  const contentByLang = useMemo(
    () => ({
      English: {
        heading: "Please choose an option!",
        toggleLabel: "English",
        purchaseTitle: "Purchase",
        purchaseDesc: "Find, Finance, and Forge Your Future with Our Home Purchase Loans.",
        refinanceTitle: "Refinance",
        refinanceDesc: "Lower Your Payments and Boost Your Budget with Our Refinance Options.",
        appointmentTitle: "Schedule Appointment",
        appointmentDesc: "Schedule Your Consultation and Move Closer to Mortgage Solutions",
      },
      Spanish: {
        heading: "¡Por favor elija una opción!",
        toggleLabel: "Spanish",
        purchaseTitle: "Comprar",
        purchaseDesc:
          "Encuentre, financie y construya su futuro con nuestros préstamos para compra de vivienda.",
        refinanceTitle: "Refinanciar",
        refinanceDesc:
          "Reduzca sus pagos y mejore su presupuesto con nuestras opciones de refinanciamiento.",
        appointmentTitle: "Programar Cita",
        appointmentDesc:
          "Programe su consulta y acérquese a soluciones hipotecarias",
      },
    }),
    []
  );

  const getDynamicHeading = useCallback((utm_term) => {
    const defaultHeading = contentByLang[lang].heading;

    const normalizedKw = String(utm_term || "").trim().toLowerCase();
    if (
      !normalizedKw ||
      normalizedKw === "direct" ||
      normalizedKw === "null" ||
      normalizedKw === "undefined"
    ) {
      return defaultHeading;
    }
    
    const formattedKw = utm_term.toUpperCase();
    const kwHeading = lang === 'English' ? `${formattedKw}` : `${formattedKw}`;
    
    // Show both default heading and kw parameter
    return `${defaultHeading} - ${kwHeading}`;
  }, [contentByLang, lang]);

  const pageContent = contentByLang[lang];
  const dynamicHeading = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const kwFromUrl = searchParams.get("utm_term");
    const utm_term = kwFromUrl || localStorage.getItem("utm_term");
    return getDynamicHeading(utm_term);
  }, [getDynamicHeading, location.search]);

  const headerKw = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const kwFromUrl = searchParams.get("utm_term");
    const utm_term = kwFromUrl || localStorage.getItem("utm_term") || "";
    const normalizedKw = String(utm_term || "").trim().toLowerCase();
    const isValidKw =
      !!normalizedKw &&
      normalizedKw !== "direct" &&
      normalizedKw !== "null" &&
      normalizedKw !== "undefined";
    return {
      isValidKw,
      formattedKw: isValidKw ? String(utm_term).toUpperCase() : "",
    };
  }, [location.search]);

  const utmType = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const utmTypeFromUrl = searchParams.get("utm_type");
    return String(utmTypeFromUrl || "").trim().toLowerCase();
  }, [location.search]);

  const showPurchaseCard = utmType !== "refinance";
  const showRefinanceCard = utmType !== "purchase";

  return (
    <div className="home-container">
      <div className="flow-selection">
        <div>
          <div className="title-section">
            <h1 className="title">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {headerKw.isValidKw ? (
                  <>
                    <span className="title-fontWeight--700" style={{ fontSize: '2em', marginBottom: '23px' }}>
                      {headerKw.formattedKw}
                    </span>
                    <span className="title-fontWeight--700" style={{ fontSize: '1.3em' }}>
                      {contentByLang[lang].heading}
                    </span>
                  </>
                ) : (
                  <span className="title-fontWeight--700" style={{ fontSize: '1.8em' }}>
                    {dynamicHeading}
                  </span>
                )}
              </div>
            </h1>
          </div>
          <div className="card-selection">
            {showPurchaseCard && (
              <div
                className="card-wrapper"
                onClick={() => handleCardClick("purchase")}
                role="button"
                style={{ cursor: "pointer" }}
              >
                <CardComponent
                  cardName={pageContent.purchaseTitle}
                  icon={PurchaseIcon}
                  cardContent={pageContent.purchaseDesc}
                />
              </div>
            )}
            {showRefinanceCard && (
              <div
                className="card-wrapper"
                onClick={() => handleCardClick("refinance")}
                role="button"
                style={{ cursor: "pointer" }}
              >
                <CardComponent
                  cardName={pageContent.refinanceTitle}
                  icon={RefinanceIcon}
                  cardContent={pageContent.refinanceDesc}
                />
              </div>
            )}
            <div
              className="card-wrapper"
              onClick={() => handleCardClick("appointment")}
              role="button"
              style={{ cursor: "pointer" }}
            >
              <CardComponent
                cardName={pageContent.appointmentTitle}
                icon={() => <img src={appointmenticon} alt="Appointment" style={{ width: 80, height: 80 }} />}
                cardContent={pageContent.appointmentDesc}
              />
            </div>
           
          </div>
        </div>
        {/* <div className="image-section">
          <img
            src={require("../../assets/dashboardImage.png")}
            alt="Dashboard Image"
            width="70%"

          />
          <h1
            className="title-fontWeight--600"
            style={{ marginBottom: "1rem" }}
          >
            Approx. 5 Min
          </h1>
          <p style={{ textAlign: "center" }}>
            Answer Few Questions in order to get you the right data that is
            tailor made for you. Which{" "}
            <strong className="title-fontWeight--600">
              will take approx. 5 Min
            </strong>
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default Home;
